import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import { paymentEftBelongsToAgent } from "../utils/agentReportUtils";
import {
  agentFilterKey,
  buildAgentSummaryRows,
  buildTotalDetailsRows,
  currentYearRange,
  filterRowsForYear,
  formatIsoDisplay,
  formatMoney,
  mapEftRows,
} from "../utils/payrollEftReportUtils";

const REPORT_LABELS = {
  "agent-summary": "Agent Summary",
  "total-details": "Total Details",
};

const thClass =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 whitespace-nowrap";
const thRightClass =
  "px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 whitespace-nowrap";
const tdClass = "px-4 py-3 text-sm text-gray-800 whitespace-nowrap";
const tdRightClass =
  "px-4 py-3 text-sm text-gray-800 text-right tabular-nums whitespace-nowrap";
const tdMutedClass = "px-4 py-3 text-sm text-gray-500 whitespace-nowrap";

export default function PayrollEftReports() {
  const [searchParams] = useSearchParams();
  const { appUser, isAgent, isAdmin, isSuperUser } = useRole();
  const isOfficeUser = isAdmin || isSuperUser;
  const reportType =
    searchParams.get("type") === "total-details"
      ? "total-details"
      : "agent-summary";

  const yearRange = useMemo(() => currentYearRange(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawRows, setRawRows] = useState([]);
  /** @type {[Record<string, boolean>, Function]} */
  const [selectedAgents, setSelectedAgents] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [eftRes, agentsRes] = await Promise.all([
          axiosInstance.get("/commission-trust-eft"),
          axiosInstance.get("/agents"),
        ]);
        if (cancelled) return;
        const agents = Array.isArray(agentsRes.data) ? agentsRes.data : [];
        let efts = (Array.isArray(eftRes.data) ? eftRes.data : []).filter(
          (eft) => eft.type === "AgentCommissionTransfer",
        );

        if (isAgent && !isOfficeUser) {
          if (!appUser) {
            throw new Error("Not signed in as an agent.");
          }
          efts = efts.filter((eft) => paymentEftBelongsToAgent(eft, appUser));
        }

        setRawRows(mapEftRows(efts, agents));
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load EFT report data.",
          );
          setRawRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appUser, isAgent, isOfficeUser]);

  const yearRows = useMemo(
    () => filterRowsForYear(rawRows, yearRange.fromIso, yearRange.toIso),
    [rawRows, yearRange.fromIso, yearRange.toIso],
  );

  /** Agents paid in the current year (for office filter checkboxes). */
  const paidAgents = useMemo(() => {
    const byKey = new Map();
    for (const row of yearRows) {
      const key = agentFilterKey(row);
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          agentId: row.agentId || "",
          agentName: row.agentName || "",
        });
      }
    }
    return [...byKey.values()].sort((a, b) =>
      String(a.agentName).localeCompare(String(b.agentName), undefined, {
        sensitivity: "base",
      }),
    );
  }, [yearRows]);

  // Default: all paid agents checked when the paid-agent list changes.
  useEffect(() => {
    if (!isOfficeUser) return;
    setSelectedAgents((prev) => {
      const next = {};
      for (const agent of paidAgents) {
        next[agent.key] =
          prev[agent.key] !== undefined ? prev[agent.key] : true;
      }
      return next;
    });
  }, [paidAgents, isOfficeUser]);

  const filteredYearRows = useMemo(() => {
    if (!isOfficeUser) return yearRows;
    return yearRows.filter((row) => selectedAgents[agentFilterKey(row)]);
  }, [yearRows, selectedAgents, isOfficeUser]);

  const agentSummary = useMemo(
    () => buildAgentSummaryRows(filteredYearRows),
    [filteredYearRows],
  );

  const agentSummaryTotals = useMemo(() => {
    return agentSummary.reduce(
      (acc, row) => ({
        totalPayment: acc.totalPayment + (Number(row.totalPayment) || 0),
        netCommission: acc.netCommission + (Number(row.netCommission) || 0),
        hst: acc.hst + (Number(row.hst) || 0),
      }),
      { totalPayment: 0, netCommission: 0, hst: 0 },
    );
  }, [agentSummary]);

  const totalDetails = useMemo(
    () => buildTotalDetailsRows(filteredYearRows),
    [filteredYearRows],
  );

  const toggleAgent = (key) => {
    setSelectedAgents((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectAllAgents = () => {
    const next = {};
    for (const agent of paidAgents) next[agent.key] = true;
    setSelectedAgents(next);
  };

  const clearAllAgents = () => {
    const next = {};
    for (const agent of paidAgents) next[agent.key] = false;
    setSelectedAgents(next);
  };

  const showAgentSidebar =
    isOfficeUser && !loading && paidAgents.length > 0;

  const agentSidebar = showAgentSidebar ? (
    <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0 rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col max-h-[70vh] lg:max-h-[calc(100vh-12rem)]">
      <div className="px-3 py-3 border-b border-slate-200 flex-shrink-0">
        <h2 className="text-sm font-semibold text-slate-800">
          Agents paid in {yearRange.year}
        </h2>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={selectAllAgents}
            className="text-xs font-medium text-blue-900 hover:underline"
          >
            Select all
          </button>
          <span className="text-slate-300">|</span>
          <button
            type="button"
            onClick={clearAllAgents}
            className="text-xs font-medium text-slate-600 hover:underline"
          >
            Clear all
          </button>
        </div>
      </div>
      <div className="overflow-y-auto p-2 space-y-0.5">
        {paidAgents.map((agent) => (
          <label
            key={agent.key}
            className="flex items-start gap-2 rounded-lg px-2 py-2 text-sm text-slate-800 hover:bg-slate-50 cursor-pointer"
          >
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300 text-blue-900 focus:ring-blue-800 flex-shrink-0"
              checked={Boolean(selectedAgents[agent.key])}
              onChange={() => toggleAgent(agent.key)}
            />
            <span className="leading-snug break-words">
              {agent.agentId ? (
                <span className="text-slate-500">#{agent.agentId} · </span>
              ) : null}
              {agent.agentName || "Unknown"}
            </span>
          </label>
        ))}
      </div>
    </aside>
  ) : null;

  const summaryTable = (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm min-w-0 flex-1">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className={thClass}>Agent #</th>
              <th className={thClass}>Name</th>
              <th className={thRightClass}>
                Commission Paid
                <span className="block font-normal normal-case tracking-normal text-[11px] text-slate-500">
                  (Excluding HST)
                </span>
              </th>
              <th className={thRightClass}>
                Commission Paid
                <span className="block font-normal normal-case tracking-normal text-[11px] text-slate-500">
                  (Including HST)
                </span>
              </th>
              <th className={thRightClass}>HST</th>
            </tr>
          </thead>
          <tbody>
            {agentSummary.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-10 text-center text-sm text-slate-500"
                >
                  No EFT payments for {yearRange.year}.
                </td>
              </tr>
            ) : (
              agentSummary.map((row, idx) => (
                <tr
                  key={row.agentKey || `${row.agentId}-${row.agentName}`}
                  className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}
                >
                  <td className={tdClass}>{row.agentId}</td>
                  <td className={`${tdClass} font-medium`}>{row.agentName}</td>
                  <td className={tdRightClass}>
                    {formatMoney(row.totalPayment)}
                  </td>
                  <td className={tdRightClass}>
                    {formatMoney(row.netCommission)}
                  </td>
                  <td className={tdRightClass}>{formatMoney(row.hst)}</td>
                </tr>
              ))
            )}
          </tbody>
          {agentSummary.length > 0 ? (
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-100">
                <td className={`${tdClass} font-semibold`} colSpan={2}>
                  Total
                </td>
                <td className={`${tdRightClass} font-semibold`}>
                  {formatMoney(agentSummaryTotals.totalPayment)}
                </td>
                <td className={`${tdRightClass} font-semibold`}>
                  {formatMoney(agentSummaryTotals.netCommission)}
                </td>
                <td className={`${tdRightClass} font-semibold`}>
                  {formatMoney(agentSummaryTotals.hst)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );

  const detailsTable = (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm min-w-0 flex-1">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <colgroup>
            <col className="w-[12%]" />
            <col className="w-[46%]" />
            <col className="w-[18%]" />
            <col className="w-[24%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className={thClass}>Trade #</th>
              <th className={thClass}>Address</th>
              <th className={thClass}>EFT issued date</th>
              <th className={thRightClass}>Total Payment</th>
            </tr>
          </thead>
          <tbody>
            {totalDetails.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-10 text-center text-sm text-slate-500"
                >
                  No EFT payments for {yearRange.year}.
                </td>
              </tr>
            ) : (
              totalDetails.map((row, idx) => (
                <tr
                  key={`${row.tradeNumber}-${row.closingDate}-${idx}`}
                  className={
                    idx % 2 === 0
                      ? "bg-white border-b border-slate-100"
                      : "bg-slate-50/60 border-b border-slate-100"
                  }
                >
                  <td className={tdClass}>{row.tradeNumber}</td>
                  <td className={`${tdClass} whitespace-normal max-w-md`}>
                    {row.address || "—"}
                  </td>
                  <td className={tdMutedClass}>
                    {formatIsoDisplay(row.issuedDate) || "—"}
                  </td>
                  <td className={tdRightClass}>
                    {formatMoney(row.totalPayment)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            EFT Reports — {REPORT_LABELS[reportType]}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Period: {yearRange.label}
          </p>
        </div>

        {error ? (
          <div
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-sm text-slate-500 shadow-sm">
            Loading…
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-start">
            {agentSidebar}
            {reportType === "agent-summary" ? summaryTable : detailsTable}
          </div>
        )}
      </div>
    </div>
  );
}
